import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SectionNav from '../../../components/layout/SectionNav';
import { researchTabs } from '../../../nav/publicNav';
import { Search, FileText, X, Loader2, TrendingUp, Target, ShieldAlert, CalendarDays } from 'lucide-react';
import { useIndiaPitchStore } from '../../../state/indiaPitchStore';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import PageHeader from '../../../components/layout/PageHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SEARCH_API = `${BACKEND_URL}/api/india/securities`;

const PLAYBOOK_FALLBACK = [
  { id: 'equity_pitch', name: 'Equity Pitch Report', description: 'Full institutional-grade equity research report' },
  { id: 'earnings_update', name: 'Earnings Update Note', description: 'Post-results summary with margin changes & outlook' },
  { id: 'initiation_note', name: 'Stock Initiation Note', description: 'First-coverage deep dive with target price' },
  { id: 'portfolio_review', name: 'Portfolio Risk Review', description: 'Concentration, sector tilt, rebalancing suggestions' },
  { id: 'client_monthly', name: 'Client Monthly Report', description: 'Performance vs benchmark, top movers, action items' },
];
const PLAYBOOK_META = {
  equity_pitch: { icon: FileText, accent: 'from-blue-500/20 to-blue-600/10', color: 'text-blue-500' },
  earnings_update: { icon: TrendingUp, accent: 'from-emerald-500/20 to-emerald-600/10', color: 'text-emerald-500' },
  initiation_note: { icon: Target, accent: 'from-purple-500/20 to-purple-600/10', color: 'text-purple-500' },
  portfolio_review: { icon: ShieldAlert, accent: 'from-amber-500/20 to-amber-600/10', color: 'text-amber-500' },
  client_monthly: { icon: CalendarDays, accent: 'from-rose-500/20 to-rose-600/10', color: 'text-rose-500' },
};

export default function ReportsHubPage() {
  const navigate = useNavigate();
  const {
    allReports, fetchAllReports, generateReport, status, generating, playbooks, fetchPlaybooks,
  } = useIndiaPitchStore();

  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [selectedPlaybook, setSelectedPlaybook] = useState('equity_pitch');
  const [generating_, setGenerating] = useState(false);
  const suggestTimer = useRef(null);
  const suggestRef = useRef(null);

  useEffect(() => {
    fetchAllReports();
    fetchPlaybooks();
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (suggestRef.current && !suggestRef.current.contains(e.target))
        setShowSuggestions(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearchChange = useCallback((value) => {
    setSearchInput(value);
    clearTimeout(suggestTimer.current);
    if (!value.trim() || value.trim().length < 1) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    suggestTimer.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(SEARCH_API, { params: { search: value.trim(), page_size: 8 } });
        const items = data.items || [];
        setSuggestions(items);
        if (items.length > 0) setShowSuggestions(true);
      } catch {
        setSuggestions([]);
      }
    }, 200);
  }, []);

  const handleSelectStock = (stock) => {
    const sym = stock.nse_symbol || stock.bse_code;
    setSelectedStock({
      isin: stock.isin,
      symbol: sym,
      name: stock.company_name,
    });
    setSearchInput(`${sym} — ${stock.company_name}`);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const isPortfolioPlaybook = ['portfolio_review', 'client_monthly'].includes(selectedPlaybook);
  const pbLabel = selectedPlaybook === 'equity_pitch' ? 'Pitch Report'
    : selectedPlaybook.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const [genStatus, setGenStatus] = useState(null); // null | 'running' | 'success' | 'failed'

  const handleGenerate = async () => {
    const pb = PLAYBOOK_META[selectedPlaybook] ? selectedPlaybook : 'equity_pitch';
    if (!isPortfolioPlaybook && !selectedStock?.isin) return;
    setGenerating(true);
    setGenStatus('running');
    try {
      const result = await generateReport(isPortfolioPlaybook ? null : selectedStock.isin, pb);
      if (pb === 'equity_pitch' && selectedStock?.symbol) {
        navigate(`/public/india-company/${selectedStock.symbol}/pitch`);
        return;
      }
      // Poll for completion on non-equity playbooks
      const runId = result?.run_id;
      if (runId) {
        const pollInterval = setInterval(async () => {
          try {
            const { data } = await axios.get(`${BACKEND_URL}/api/india/reports/status/${runId}`);
            if (data.status === 'SUCCESS') {
              clearInterval(pollInterval);
              setGenerating(false);
              setGenStatus('success');
              fetchAllReports();
              setTimeout(() => setGenStatus(null), 5000);
            } else if (data.status === 'FAILED') {
              clearInterval(pollInterval);
              setGenerating(false);
              setGenStatus('failed');
              setTimeout(() => setGenStatus(null), 5000);
            }
          } catch { /* keep polling */ }
        }, 3000);
        // Safety timeout after 3 minutes
        setTimeout(() => { clearInterval(pollInterval); setGenerating(false); setGenStatus(null); }, 180000);
      }
    } catch {
      setGenerating(false);
      setGenStatus('failed');
      setTimeout(() => setGenStatus(null), 5000);
    }
  };

  const recColor = (style) =>
    style === 'Positive' ? 'bg-emerald-600 text-white' :
    style === 'Cautious' ? 'bg-amber-500 text-white' :
    style === 'Neutral' ? 'bg-blue-500 text-white' : 'bg-muted';

  const isGenerating = generating_ || generating || status === 'RUNNING';

  return (
    <div className="space-y-4">
      <SectionNav tabs={researchTabs} />
      <PageHeader
        title="Reports & Playbooks"
        description="Generate institutional-grade reports — equity pitches, earnings notes, portfolio reviews, and more"
      />

      {/* Playbook Selector */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-4">
        {(playbooks.length > 0 ? playbooks : PLAYBOOK_FALLBACK).map((pb) => {
          const meta = PLAYBOOK_META[pb.id] || PLAYBOOK_META.equity_pitch;
          const Icon = meta.icon;
          const active = selectedPlaybook === pb.id;
          return (
            <button
              key={pb.id}
              onClick={() => setSelectedPlaybook(pb.id)}
              className={`flex flex-col items-start gap-1.5 p-3 rounded-xl border text-left transition-all ${
                active ? 'border-emerald-500 bg-emerald-500/5 shadow-sm' : 'border-border hover:border-border/80 hover:bg-secondary/30'
              }`}
            >
              <Icon className={`w-4 h-4 ${active ? 'text-emerald-500' : meta.color}`} />
              <p className={`text-xs font-medium leading-tight ${active ? 'text-emerald-600 dark:text-emerald-400' : ''}`}>{pb.name}</p>
              {pb.description && <p className="text-[10px] text-muted-foreground leading-snug line-clamp-2">{pb.description}</p>}
            </button>
          );
        })}
      </div>

      {/* Search + Generate */}
      <Card className="mb-6">
        <CardContent className="p-4 space-y-3">
          {isPortfolioPlaybook ? (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-secondary/30 border border-border/50">
              <ShieldAlert className="w-4 h-4 text-amber-500 shrink-0" />
              <p className="text-xs text-muted-foreground">This report uses your portfolio holdings — no stock search needed.</p>
            </div>
          ) : (
            <div className="relative" ref={suggestRef}>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by company name, symbol, or ISIN…"
                className="pl-9 pr-8 text-sm"
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              />
              {searchInput && (
                <button
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => { setSearchInput(''); setSuggestions([]); setShowSuggestions(false); setSelectedStock(null); }}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-50 top-full mt-1 left-0 w-full max-h-72 overflow-y-auto overflow-x-hidden bg-popover border border-border rounded-xl shadow-2xl">
                  {suggestions.map((s) => {
                    const sym = s.nse_symbol || s.bse_code;
                    return (
                      <button
                        key={s.isin || sym}
                        className="block w-full text-left px-3 py-2.5 hover:bg-secondary border-b border-border/50 last:border-0 transition-colors overflow-hidden"
                        onClick={() => handleSelectStock(s)}
                      >
                        <p className="text-xs font-bold text-emerald-600 dark:text-emerald-400 leading-snug">{sym}</p>
                        <p className="text-xs text-foreground truncate leading-snug">{s.company_name}</p>
                        {s.ai_industry && (
                          <p className="text-[10px] text-muted-foreground truncate leading-snug">{s.ai_industry}</p>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <Button
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
            disabled={(!isPortfolioPlaybook && !selectedStock) || isGenerating}
            onClick={handleGenerate}
          >
            {isGenerating ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</>
            ) : (
              <><FileText className="w-4 h-4 mr-2" /> Generate {pbLabel}</>
            )}
          </Button>

          {/* Status feedback */}
          {genStatus === 'running' && (
            <div className="flex items-center gap-2 justify-center text-xs text-muted-foreground py-1">
              <Loader2 className="w-3 h-3 animate-spin" />
              Generating {pbLabel}… This may take 1-2 minutes.
            </div>
          )}
          {genStatus === 'success' && (
            <p className="text-xs text-emerald-600 text-center py-1">Report generated successfully! See it below.</p>
          )}
          {genStatus === 'failed' && (
            <p className="text-xs text-red-500 text-center py-1">Generation failed. Try again — Claude may have been overloaded.</p>
          )}
        </CardContent>
      </Card>

      {/* Report History */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Generated Reports</h2>

        {(!allReports || allReports.length === 0) && (
          <Card>
            <CardContent className="p-8 text-center space-y-2">
              <FileText className="w-10 h-10 mx-auto text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">No reports generated yet. Search for a stock above to create your first report.</p>
            </CardContent>
          </Card>
        )}

        {allReports && allReports.length > 0 && (
          <div className="space-y-2">
            {allReports.map((rpt) => (
              <button
                key={rpt.run_id}
                className="w-full text-left"
                onClick={() => {
                  const sym = rpt.nse_symbol || rpt.isin;
                  navigate(`/public/india-company/${sym}/pitch`);
                }}
              >
                <Card className="hover:border-emerald-400 dark:hover:border-emerald-700 transition-colors">
                  <CardContent className="p-4 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{rpt.company_name || rpt.nse_symbol}</span>
                        {rpt.nse_symbol && (
                          <Badge variant="outline" className="text-[10px]">{rpt.nse_symbol}</Badge>
                        )}
                        {rpt.playbook_id && rpt.playbook_id !== 'equity_pitch' && (
                          <Badge variant="secondary" className="text-[10px]">
                            {rpt.playbook_id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {rpt.completed_at ? new Date(rpt.completed_at).toLocaleDateString('en-IN', {
                          day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
                        }) : '—'}
                      </p>
                    </div>
                    {rpt.recommendation_style && (
                      <Badge className={`${recColor(rpt.recommendation_style)} text-xs px-3`}>
                        {rpt.recommendation_style}
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
